'use client';

import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { LANGUAGES } from '@/src/types/flashcard';
import { Popover, PopoverContent, PopoverTrigger } from '@/src/components/ui/popover';
import { Button } from '@/src/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/src/components/ui/command';
import { cn } from '@/lib/utils';

interface LanguageComboboxProps {
  value: string;
  onChange: (value: string) => void;
  allowEmpty?: boolean;
  emptyLabel?: string;
  placeholder?: string;
  className?: string;
}

export function LanguageCombobox({
  value,
  onChange,
  allowEmpty = false,
  emptyLabel = 'All languages',
  placeholder = 'Select language...',
  className,
}: LanguageComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = LANGUAGES.find((l) => l.value === value);
  const displayLabel = value === '' && allowEmpty ? emptyLabel : (selected?.label ?? placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
        >
          {displayLabel}
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent style={{ width: 'var(--radix-popover-trigger-width)' }} className='p-0' align='start'>
        <Command>
          <CommandInput placeholder='Search language...' />
          <CommandList>
            <CommandEmpty>No language found.</CommandEmpty>
            <CommandGroup>
              {allowEmpty && (
                <CommandItem
                  value={emptyLabel}
                  onSelect={() => {
                    onChange('');
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === '' ? 'opacity-100' : 'opacity-0')} />
                  {emptyLabel}
                </CommandItem>
              )}
              {LANGUAGES.map((l) => (
                <CommandItem
                  key={l.value}
                  value={l.label}
                  onSelect={() => {
                    onChange(l.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', value === l.value ? 'opacity-100' : 'opacity-0')}
                  />
                  {l.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
